import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import Table from "@mui/joy/Table";
import { LineChart } from "@mui/x-charts/LineChart";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    ScrollTable,
    WatchSignals,
} from "../../bids/analytics/components/AnalyticsShared";
import { useAsyncBlock } from "../../bids/analytics/hooks";
import {
    formatInt,
    formatMoneyKopecks,
    formatRatePercent,
    toNum,
} from "../../bids/analytics/format";
import { UeFilters, UeFiltersState } from "../components/UeFilters";
import { TermHint, TermHintsRow } from "../components/TermHint";
import { ueOverviewApi } from "../unitEconomicsApi";
import { UeSeriesPoint } from "../../../models/unitEconomics";

type Props = {
    isActive: boolean;
    filters: UeFiltersState;
    onFiltersChange: (next: UeFiltersState) => void;
};

function seriesToDataset(points: UeSeriesPoint[], key: string) {
    return (points ?? []).map((p) => ({
        x: new Date(p.bucketUtc),
        [key]: key === "takeRate" ? toNum(p.doubleValue) * 100 : toNum(p.value),
    }));
}

function mergeSeries(
    seriesList: Array<{ key: string; points: UeSeriesPoint[]; useDouble?: boolean }>
) {
    const map = new Map<number, Record<string, any>>();
    for (const s of seriesList) {
        for (const p of s.points ?? []) {
            const t = new Date(p.bucketUtc).getTime();
            const row = map.get(t) ?? { x: new Date(p.bucketUtc) };
            row[s.key] = s.useDouble ? toNum(p.doubleValue) * 100 : toNum(p.value) / 100;
            map.set(t, row);
        }
    }
    return Array.from(map.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, v]) => v);
}

export function OverviewTab(props: Props) {
    const { isActive, filters, onFiltersChange } = props;
    const [state, refresh] = useAsyncBlock(
        () => ueOverviewApi({ from: filters.from, to: filters.to }),
        isActive,
        [filters.from, filters.to]
    );

    const data = state.data;
    const waterfall = React.useMemo(() => {
        if (!data) return [];
        return mergeSeries([
            { key: "orders", points: data.orderProfitSeries },
            { key: "deposits", points: data.depositProfitSeries },
            { key: "withdrawals", points: data.withdrawalProfitSeries },
            { key: "adjustments", points: data.adjustmentProfitSeries },
        ]);
    }, [data]);

    const takeRateDs = React.useMemo(
        () => seriesToDataset(data?.takeRateSeries ?? [], "takeRate"),
        [data]
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <UeFilters value={filters} onChange={onFiltersChange} onRefresh={refresh} loading={state.loading} />

            {state.loading && !data && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {!state.loading && data && data.orderCount === 0 && data.totalProfit === 0 && (
                <EmptyState hint="За период нет ордеров и прибыли. Расширьте даты." />
            )}

            {data && (
                <AnalyticsCard
                    title={
                        <>
                            Обзор <TermHint term="unitEconomics">юнит-экономики</TermHint>
                        </>
                    }
                    subtitle={
                        <>
                            <TermHint term="takeRate" />, структура прибыли,{" "}
                            <TermHintsRow terms={["arpu", "arppu"]} /> и средний чек (
                            <TermHint term="aov" />) за период.
                        </>
                    }
                    snapshotLabel="period"
                    howToRead={
                        <List size="sm" marker="disc">
                            <ListItem>
                                <Typography level="body-sm">
                                    <TermHint term="takeRate" /> — доля торговой комиссии от{" "}
                                    <TermHint term="gmv" /> ордеров
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <TermHint term="waterfall" /> — вклад ордеров, депозитов, выводов и
                                    корректировок в прибыль
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <TermHintsRow terms={["arpu", "arppu"]} /> — прибыль на активного /
                                    платящего пользователя
                                </Typography>
                            </ListItem>
                            <ListItem>
                                <Typography level="body-sm">
                                    <TermHint term="aov" /> — средний <TermHint term="gmv" /> одного
                                    ордера; <TermHint term="fee" /> / ордер — средняя комиссия
                                </Typography>
                            </ListItem>
                        </List>
                    }
                    watchFor={
                        <WatchSignals
                            items={[
                                {
                                    signal: <TermHint term="takeRate" />,
                                    ifGrows: "платформа зарабатывает больше с оборота (тарифы/микс дороже).",
                                    ifFalls: "маржа с GMV сжимается — проверьте комиссии и скидки.",
                                },
                                {
                                    signal: "Общая прибыль",
                                    ifGrows: "бизнес генерирует больше contribution за период.",
                                    ifFalls: "просадка по ордерам, платежам или рост отрицательных корректировок.",
                                },
                                {
                                    signal: <TermHintsRow terms={["arpu", "arppu"]} />,
                                    ifGrows: "средний пользователь становится ценнее.",
                                    ifFalls: "больше «пустой» активности или меньше платящих.",
                                },
                                {
                                    signal: <TermHint term="aov" />,
                                    ifGrows: "средний чек растёт — хороший рычаг GMV без роста юзеров.",
                                    ifFalls: "сделки мельчают; смотрите микс игр и цен.",
                                },
                            ]}
                        />
                    }
                >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        <MetricHint
                            label={<TermHint term="takeRate" />}
                            value={formatRatePercent(data.takeRate)}
                            hint={`${formatMoneyKopecks(data.orderProfit)} / ${formatMoneyKopecks(data.orderGmv)}`}
                            color="primary"
                        />
                        <MetricHint label="Прибыль всего" value={formatMoneyKopecks(data.totalProfit)} color="success" />
                        <MetricHint
                            label="Прибыль ордеров"
                            value={formatMoneyKopecks(data.orderProfit)}
                            hint={`деп. ${formatMoneyKopecks(data.depositProfit)} · выв. ${formatMoneyKopecks(data.withdrawalProfit)}`}
                        />
                        <MetricHint
                            label={<TermHint term="arpu" />}
                            value={formatMoneyKopecks(data.arpu)}
                            hint={`${formatInt(data.activeUsers)} акт.`}
                        />
                        <MetricHint
                            label={<TermHint term="arppu" />}
                            value={formatMoneyKopecks(data.arppu)}
                            hint={`${formatInt(data.payingUsers)} плат.`}
                            color="success"
                        />
                        <MetricHint
                            label={<TermHint term="aov" />}
                            value={formatMoneyKopecks(data.averageOrderValue)}
                            hint={`${formatInt(data.orderCount)} ордеров`}
                        />
                        <MetricHint
                            label={
                                <>
                                    <TermHint term="fee" /> / ордер
                                </>
                            }
                            value={formatMoneyKopecks(data.averageFeePerOrder)}
                        />
                    </Box>

                    <Typography level="title-sm" sx={{ mt: 1 }}>
                        Структура прибыли по дням (₽) — <TermHint term="waterfall" />
                    </Typography>
                    {waterfall.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <LineChart
                            height={280}
                            dataset={waterfall}
                            xAxis={[{ dataKey: "x", scaleType: "time" }]}
                            series={[
                                { dataKey: "orders", label: "Ордера", color: "#1976d2", stack: "p", area: true },
                                { dataKey: "deposits", label: "Депозиты", color: "#2e7d32", stack: "p", area: true },
                                { dataKey: "withdrawals", label: "Выводы", color: "#ed6c02", stack: "p", area: true },
                                { dataKey: "adjustments", label: "Корректировки", color: "#9c27b0", stack: "p", area: true },
                            ]}
                            margin={{ left: 70, right: 20, top: 20, bottom: 40 }}
                        />
                    )}

                    <Typography level="title-sm" sx={{ mt: 1 }}>
                        <TermHint term="takeRate" /> по дням (%)
                    </Typography>
                    {takeRateDs.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <LineChart
                            height={240}
                            dataset={takeRateDs}
                            xAxis={[{ dataKey: "x", scaleType: "time" }]}
                            series={[
                                {
                                    dataKey: "takeRate",
                                    label: "Take Rate %",
                                    color: "#1565c0",
                                    valueFormatter: (v) =>
                                        v == null ? "—" : `${Number(v).toFixed(2)}%`,
                                },
                            ]}
                            margin={{ left: 60, right: 20, top: 20, bottom: 40 }}
                        />
                    )}

                    <Typography level="title-sm" sx={{ mt: 1 }}>
                        <TermHint term="waterfall" /> итог за период
                    </Typography>
                    <ScrollTable>
                        <Table size="sm" hoverRow>
                            <thead>
                                <tr>
                                    <th>Источник</th>
                                    <th style={{ textAlign: "right" }}>Сумма</th>
                                    <th style={{ textAlign: "right" }}>Доля</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { key: "orders", name: <>Ордера (комиссии)</>, v: data.orderProfit },
                                    {
                                        key: "dep",
                                        name: (
                                            <>
                                                Депозиты (<TermHint term="ourBenefit" />)
                                            </>
                                        ),
                                        v: data.depositProfit,
                                    },
                                    {
                                        key: "wd",
                                        name: (
                                            <>
                                                Выводы (<TermHint term="ourBenefit" />)
                                            </>
                                        ),
                                        v: data.withdrawalProfit,
                                    },
                                    { key: "adj", name: <>Корректировки</>, v: data.adjustmentProfit },
                                ].map((r) => (
                                    <tr key={r.key}>
                                        <td>{r.name}</td>
                                        <td style={{ textAlign: "right" }}>{formatMoneyKopecks(r.v)}</td>
                                        <td style={{ textAlign: "right" }}>
                                            {formatRatePercent(
                                                data.totalProfit !== 0 ? r.v / data.totalProfit : 0
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                <tr>
                                    <td>
                                        <strong>Итого</strong>
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        <strong>{formatMoneyKopecks(data.totalProfit)}</strong>
                                    </td>
                                    <td style={{ textAlign: "right" }}>100%</td>
                                </tr>
                            </tbody>
                        </Table>
                    </ScrollTable>
                </AnalyticsCard>
            )}
        </Box>
    );
}
